"use client";

import Image from "next/image";
import { ShieldCheck, User, Car } from "lucide-react";
import PermitBarcode from "./shared/PermitBarcode";

const DriverPermitCardSkeleton = () => {
  return (
    <div className="overflow-hidden shadow-[0_4px_20px_rgba(45,101,242,0.12)] border border-[#B8CBFC] bg-[#F5F9FC] rounded-xl">
      {/* ── Modern Header ── */}
      <div className="relative bg-gradient-to-r from-[#2D65F2] to-[#1E4ED8] px-5 py-4 flex items-center justify-between overflow-hidden">
        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/20" />
          <div className="absolute right-12 bottom-0 w-20 h-20 rounded-full bg-white/10" />
        </div>

        <div className="relative z-10">
          <Image
            src="/images/logo_blue.png"
            width={120}
            height={34}
            alt="DriveStamp"
            className="brightness-0 invert"
          />
          <p className="text-[11px] text-white/90 tracking-[0.15em] uppercase mt-1 font-medium">
            Driver Permit
          </p>
        </div>

        <div className="relative z-10 w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
          <ShieldCheck size={18} className="text-white" />
        </div>
      </div>

      {/* ── Card Body ── */}
      <div className="px-5 py-5 relative">
        {/* Watermark */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden select-none">
          {Array.from({ length: 10 }).map((_, i) => (
            <span
              key={i}
              className="absolute text-[12px] font-bold tracking-widest text-[#B8CBFC]/25"
              style={{
                top: `${6 + (i % 4) * 26}%`,
                left: `${-8 + ((i * 19) % 105)}%`,
                transform: `rotate(-28deg)`,
                whiteSpace: "nowrap",
              }}
            >
              DRIVESTAMP
            </span>
          ))}
        </div>

        <div className="relative z-10 flex gap-5">
          {/* Left: Photo + Name */}
          <div className="flex flex-col items-center shrink-0">
            <div className="w-[88px] h-[108px] rounded-lg bg-white border border-[#E2E8F0] flex items-center justify-center shadow-sm">
              <div className="w-12 h-12 rounded-full bg-[#E2E8F0] flex items-center justify-center">
                <User size={26} className="text-[#94A3B8]" />
              </div>
            </div>

            {/* Name skeleton */}
            <div className="mt-3 w-[88px] space-y-1.5">
              <div className="h-2.5 bg-[#E2E8F0] rounded-full w-full animate-pulse" />
              <div className="h-2 bg-[#E2E8F0] rounded-full w-3/4 mx-auto animate-pulse" />
            </div>
          </div>

          {/* Right: Details */}
          <div className="flex-1 flex flex-col gap-3 pt-0.5">
            {/* License + Country */}
            <div className="grid grid-cols-2 gap-x-5">
              <div className="space-y-1.5">
                <div className="h-2 bg-[#CBD5E1] rounded-full w-16" />
                <div className="h-3 bg-[#E2E8F0] rounded-full w-24 animate-pulse" />
              </div>
              <div className="space-y-1.5">
                <div className="h-2 bg-[#CBD5E1] rounded-full w-14" />
                <div className="h-3 bg-[#E2E8F0] rounded-full w-20 animate-pulse" />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-x-5 border-t border-[#E2E8F0] pt-2.5">
              <div className="space-y-1.5">
                <div className="h-2 bg-[#CBD5E1] rounded-full w-20" />
                <div className="h-3 bg-[#E2E8F0] rounded-full w-16 animate-pulse" />
              </div>
              <div className="space-y-1.5">
                <div className="h-2 bg-[#CBD5E1] rounded-full w-24" />
                <div className="h-3 bg-[#E2E8F0] rounded-full w-16 animate-pulse" />
              </div>
            </div>

            {/* Vehicle */}
            <div className="border-t border-[#E2E8F0] pt-2.5">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Car size={16} className="text-[#94A3B8]" />
                <div className="h-2 bg-[#CBD5E1] rounded-full w-28" />
              </div>
              <div className="h-3.5 bg-[#E2E8F0] rounded-full w-40 mb-2 animate-pulse" />
              <div className="grid grid-cols-2 gap-x-5">
                <div className="space-y-1">
                  <div className="h-1.5 bg-[#CBD5E1] rounded-full w-10" />
                  <div className="h-2.5 bg-[#E2E8F0] rounded-full w-16 animate-pulse" />
                </div>
                <div className="space-y-1">
                  <div className="h-1.5 bg-[#CBD5E1] rounded-full w-8" />
                  <div className="h-2.5 bg-[#E2E8F0] rounded-full w-24 animate-pulse" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dashed separator */}
        <div className="border-t border-dashed border-[#B8CBFC] my-5" />

        {/* Barcode skeleton */}
        <div className="flex flex-col items-center pt-2">
          <PermitBarcode value={"CODE-847293-01654"} />
        </div>
      </div>
    </div>
  );
};

export default DriverPermitCardSkeleton;
